import { useMemo, useRef, useState, type JSX } from 'react';
import { Check, Globe2, Loader2, Plus, Sparkles, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useSuggestTerms } from '../hooks/geo';
import { flag } from './AudienceBadge';
import type { GeoGroup, TermSuggestion } from '../api/types';
import type { Platform } from '../lib/platform';

/** How the audience is measured on each platform — shown so the cost and the caveat are visible before a search. */
export const AUDIENCE_HOW: Record<Platform, string> = {
  tiktok: "Reads where each creator's commenters are from (their account country). Adds 2–3 API calls per creator that passes the other filters.",
  x: "Reads where each creator's repliers say they're from, topped up with a page of followers when replies are thin. 2–4 calls per creator.",
  instagram:
    "Instagram doesn't expose commenters, so this uses a separate audience-data service with a small monthly quota. Creators past the quota show as not measured.",
};

/**
 * "Suggest local search terms" for the audience filter.
 *
 * A topic on its own is global — "memes" returned 0 of 30 UAE creators,
 * "dubai memes" 23 of 30 — and the audience filter can only keep pages that
 * reach it. Each suggestion shows how much of one search page came from the
 * chosen countries, because a plausible-looking term can be useless.
 */
export function TermSuggestions({
  platform,
  topics: rawTopics,
  countries,
  keywordMode,
  onAddTerm,
}: {
  platform: Platform;
  /** Current keyword lines — what suggestions are built from. */
  topics: string[];
  countries: string[];
  /** Suggestions need keywords; sound searches have none. */
  keywordMode: boolean;
  onAddTerm: (term: string) => void;
}): JSX.Element {
  const suggest = useSuggestTerms();
  const [added, setAdded] = useState<Set<string>>(new Set());
  const topics = rawTopics.map((t) => t.trim()).filter(Boolean);
  const canSuggest = keywordMode && topics.length > 0 && countries.length > 0;

  if (!keywordMode) {
    return (
      <p className="text-xs text-muted-foreground">
        Term suggestions work with keyword searches. Sound searches still get the audience filter.
      </p>
    );
  }

  function add(term: string) {
    onAddTerm(term);
    setAdded((prev) => new Set(prev).add(term.toLowerCase()));
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!canSuggest || suggest.isPending}
          onClick={() => {
            setAdded(new Set());
            suggest.mutate({ topics: topics.slice(0, 5), countries });
          }}
        >
          {suggest.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {suggest.isPending ? 'Testing terms…' : 'Suggest local search terms'}
        </Button>
        <span className="text-xs text-muted-foreground">
          {topics.length === 0
            ? 'Add a keyword first — suggestions are built from it.'
            : platform !== 'tiktok'
              ? 'Tested on TikTok search (one call per term), then reused here. ~10s.'
              : 'Tests each term with one search. Takes ~10s.'}
        </span>
      </div>
      {suggest.error && <p className="text-xs text-destructive-strong">{suggest.error.message}</p>}
      {suggest.data && <SuggestionList result={suggest.data} added={added} onAdd={add} />}
    </div>
  );
}

function SuggestionList({
  result,
  added,
  onAdd,
}: {
  result: NonNullable<ReturnType<typeof useSuggestTerms>['data']>;
  added: Set<string>;
  onAdd: (term: string) => void;
}): JSX.Element {
  const measured = result.suggestions.filter((s) => s.total !== undefined);
  const untested = result.suggestions.filter((s) => s.total === undefined);
  return (
    <div className="space-y-2 rounded-lg border border-border bg-muted/30 p-3">
      {result.baseline.length > 0 && (
        <p className="text-xs text-muted-foreground">
          For comparison:{' '}
          {result.baseline.map((b, i) => (
            <span key={b.term}>
              {i > 0 && ', '}"{b.term}" alone got{' '}
              <strong className="text-foreground">
                {b.inTarget}/{b.total}
              </strong>
            </span>
          ))}{' '}
          from these countries.
        </p>
      )}
      {measured.length > 0 && (
        <ul className="space-y-1">
          {measured.map((s) => (
            <SuggestionRow key={s.term} s={s} added={added.has(s.term.toLowerCase())} onAdd={onAdd} />
          ))}
        </ul>
      )}
      {untested.length > 0 && (
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground">{untested.length} more, not tested</summary>
          <ul className="mt-1 space-y-1">
            {untested.map((s) => (
              <SuggestionRow key={s.term} s={s} added={added.has(s.term.toLowerCase())} onAdd={onAdd} />
            ))}
          </ul>
        </details>
      )}
      {result.aiNote && <p className="text-xs text-muted-foreground">{result.aiNote}</p>}
    </div>
  );
}

function SuggestionRow({ s, added, onAdd }: { s: TermSuggestion; added: boolean; onAdd: (term: string) => void }): JSX.Element {
  const share = s.share ?? 0;
  const strong = s.total !== undefined && share >= 0.5;
  const weak = s.total !== undefined && share < 0.15;
  return (
    <li className="flex items-center gap-2">
      <button
        type="button"
        disabled={added}
        onClick={() => onAdd(s.term)}
        className={cn(
          'inline-flex min-w-0 items-center gap-1 rounded-md border px-2 py-1 text-sm transition-colors',
          added ? 'border-border bg-muted text-muted-foreground' : 'border-input bg-[var(--input-bg)] hover:border-ring hover:bg-accent',
        )}
        title={added ? 'Added to keywords' : 'Add as a keyword'}
      >
        {added ? <Check className="h-3.5 w-3.5 shrink-0" /> : <Plus className="h-3.5 w-3.5 shrink-0" />}
        <span className="truncate" dir="auto">
          {s.term}
        </span>
      </button>
      <span className="text-xs text-muted-foreground">
        {flag(s.country)} {s.source === 'ai' ? 'translated' : s.source === 'name' ? 'country name' : 'local'}
      </span>
      {s.total !== undefined && (
        <span
          className={cn(
            'ml-auto shrink-0 text-xs tabular-nums',
            strong ? 'font-semibold text-success-strong' : weak ? 'text-muted-foreground' : 'text-foreground',
          )}
          title="Results from the chosen countries, out of one search page"
        >
          {s.inTarget}/{s.total} local
        </span>
      )}
    </li>
  );
}

/**
 * Type-to-find over every country plus the preset groups. The full list is
 * ~250 entries, which a plain select or checkbox list makes unusable.
 */
export function CountryPicker({
  id,
  countries,
  groups,
  loading,
  value,
  onChange,
  invalid,
}: {
  id?: string;
  countries: { code: string; name: string }[];
  groups: GeoGroup[];
  loading: boolean;
  value: string[];
  onChange: (v: string[]) => void;
  invalid?: boolean;
}): JSX.Element {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of countries) m.set(c.code, c.name);
    for (const g of groups) m.set(g.id, g.name);
    return m;
  }, [countries, groups]);

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    const chosen = new Set(value);
    // Best match first: exact name or code, then names starting with the
    // text, then anything containing it — so "india" + Enter picks India, not
    // British Indian Ocean Territory.
    const rank = (name: string, code: string) => {
      const n = name.toLowerCase();
      if (!q) return 3;
      if (n === q || code.toLowerCase() === q) return 0;
      if (n.startsWith(q)) return 1;
      if (n.split(/[\s&-]+/).some((w) => w.startsWith(q))) return 2;
      return n.includes(q) ? 3 : 9;
    };
    const g = groups
      .filter((x) => !chosen.has(x.id))
      .map((x) => ({ id: x.id, label: x.name, sub: `${x.countries.length} countries`, isGroup: true, r: rank(x.name, x.id) }));
    const c = countries
      .filter((x) => !chosen.has(x.code))
      .map((x) => ({ id: x.code, label: x.name, sub: x.code, isGroup: false, r: rank(x.name, x.code) }));
    return [...g, ...c]
      .filter((x) => x.r < 9)
      .sort((a, b) => a.r - b.r || Number(b.isGroup) - Number(a.isGroup))
      .slice(0, q ? 12 : 16);
  }, [q, countries, groups, value]);

  function pick(pickId: string) {
    onChange([...value, pickId]);
    setQuery('');
    inputRef.current?.focus();
  }

  return (
    <div className="relative">
      <div
        className={cn(
          'flex min-h-9 flex-wrap items-center gap-1 rounded-lg border bg-[var(--input-bg)] px-1.5 py-1 shadow-control transition-colors',
          'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/25',
          invalid ? 'border-destructive/70' : 'border-input',
        )}
      >
        {value.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 rounded-md bg-muted py-0.5 pl-1.5 pr-1 text-sm">
            {/^[A-Z]{2}$/.test(v) ? flag(v) : <Globe2 className="h-3.5 w-3.5 text-muted-foreground" />}
            {nameOf.get(v) ?? v}
            <button
              type="button"
              onClick={() => onChange(value.filter((x) => x !== v))}
              aria-label={`Remove ${nameOf.get(v) ?? v}`}
              title={`Remove ${nameOf.get(v) ?? v}`}
              className="rounded p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        ))}
        <input
          id={id}
          ref={inputRef}
          value={query}
          onChange={(ev) => {
            setQuery(ev.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              ev.preventDefault();
              if (matches[0]) pick(matches[0].id);
            } else if (ev.key === 'Backspace' && !query && value.length) {
              onChange(value.slice(0, -1));
            } else if (ev.key === 'Escape') {
              setOpen(false);
            }
          }}
          placeholder={value.length ? '' : loading ? 'Loading countries…' : 'United Arab Emirates, GCC…'}
          className="h-7 min-w-[8rem] flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground/70 pointer-coarse:text-base"
        />
      </div>
      {open && matches.length > 0 && (
        <ul className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-lg border border-border bg-popover p-1 shadow-overlay">
          {matches.map((m) => (
            <li key={m.id}>
              <button
                type="button"
                onMouseDown={(ev) => ev.preventDefault()}
                onClick={() => pick(m.id)}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent"
              >
                <span className="w-5 text-center">
                  {m.isGroup ? <Globe2 className="mx-auto h-3.5 w-3.5 text-muted-foreground" /> : flag(m.id)}
                </span>
                <span className="flex-1 truncate">{m.label}</span>
                <span className="text-xs text-muted-foreground">{m.sub}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
